import { Fragment, useEffect, useMemo, useState } from 'react';
import { misSolicitudesAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { KpiCard } from '@/components/charts';
import { AlertTriangle, ArrowDown, ArrowUp, ChevronsUp, Inbox, Loader2, RefreshCw, Users } from 'lucide-react';
import { FechaComprometidaCorta, ROTULO_FECHA_COMPROMETIDA } from '@/components/FechaComprometida';
import Autorizacion from '@/components/Autorizacion';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';
import { toast } from 'sonner';

/**
 * Lo que pedi, y lo que pidio mi gente.
 *
 * Hasta el 2026-08-18 quien pedia algo no tenia forma de ver en que andaba: el caso entraba
 * por mail, un agente lo registraba, y el solicitante quedaba afuera.
 *
 * Se agrupa **por capsula** porque la misma persona puede tener casos en varias y hace falta
 * distinguirlos. Como la pantalla cruza capsulas, el titulo general es generico y cada grupo
 * usa el vocabulario suyo (`unidad_de_trabajo`), que es de cada capsula.
 */

const formatoFecha = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
    });
  } catch {
    return '—';
  }
};

/**
 * Un guion es honesto; un cero es una afirmación.
 *
 * El backend manda `{pct, origen, confiable}`. `confiable` gobierna la forma: barra sólida
 * cuando el número sale de tareas o fases —está medido—, y texto apagado cuando sale del
 * estado —lo declaró la cápsula, nadie lo midió—. Son dos cosas distintas y mostrarlas igual
 * haría que la persona confíe lo mismo en las dos.
 *
 * `pct: null` se muestra como guion y no como 0%: un cero se lee como "todavía no arrancaron",
 * y sobre eso alguien escala.
 */
function Avance({ avance }) {
  const pct = avance?.pct;
  if (pct == null) {
    return (
      <span className="text-zinc-400" title="Todavía no hay avance calculado para este caso">—</span>
    );
  }
  if (!avance?.confiable) {
    return (
      <span
        className="text-[11px] text-zinc-500 tabular-nums"
        title="Estimado a partir del estado, no medido sobre las tareas"
      >
        ~{pct}%
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2 min-w-[90px]">
      <div className="flex-1 h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
        <div className="h-full bg-brand-primary" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="text-[10px] text-zinc-500 tabular-nums w-8 text-right">{pct}%</span>
    </div>
  );
}

/**
 * Con quién hablar. Es la pregunta que trae quien pidió algo y no tenía dónde mirar.
 *
 * La API ya devolvía `propietario` y `rol_propietario` —están en la whitelist del solicitante—
 * y la tabla los descartaba en el render: cinco columnas y ninguna era el dueño.
 *
 * **Un caso sin dueño se dice, no se deja en blanco.** Que esté esperando en la cola de un rol
 * es información: significa que todavía no lo tomó nadie, y es distinto de que el sistema no
 * sepa quién lo tiene.
 */
function Responsable({ caso }) {
  const nombre = caso?.propietario?.name || caso?.propietario?.id;
  const rol = caso?.rol_propietario;
  if (nombre) {
    return (
      <div className="min-w-[110px]">
        <span className="text-zinc-800">{nombre}</span>
        {rol && <span className="block text-[10px] text-zinc-400">{rol}</span>}
      </div>
    );
  }
  if (rol) {
    return (
      <span className="text-[11px] text-amber-700" title="Todavía no lo tomó nadie del rol">
        en la cola de {rol}
      </span>
    );
  }
  return <span className="text-zinc-400" title="Sin responsable ni rol asignado">—</span>;
}

/**
 * Cómo viene contra la fecha prometida.
 *
 * **El color lo decide el backend** (`relojes.semaforo_del_compromiso`) y acá sólo se pinta. Dos
 * definiciones de «esto se está por vencer» en el mismo producto es como se termina discutiendo
 * cuál tablero tiene razón.
 *
 * `sin_reloj` no se pinta: dar por cumplido lo que nadie prometió sería mentir hacia el lado que
 * nadie revisa.
 */
/**
 * El detalle de un pedido propio.
 *
 * `GET /me/solicitudes/{id}` **ya existía** —con su proyección, su re-derivación del permiso en
 * el servidor y su 404 en vez de 403 para no confirmar que el caso existe— y **ninguna pantalla
 * lo llamaba**. Esto es esa pantalla.
 *
 * **Lo que muestra es exactamente lo que la whitelist permite**, y nada más. No hay una decisión
 * de privacidad nueva acá: se dibuja lo que el servidor ya decidió que esta persona puede ver.
 *
 * **Lo que falta y no se inventó:** el hilo de respuestas. Requiere decidir qué parte de la
 * conversación puede leer quien pidió el caso —la suya y la del agente sí; lo que los agentes se
 * dicen entre ellos, no— y eso es una decisión de producto, no un `find`.
 */
/**
 * Como se llama un estado, segun el catalogo que manda el backend.
 *
 * **No es un mapa escrito aca.** La tabla imprimia el id tecnico —`cancelled`— a quien pidio el
 * caso. Los ids son del sistema y los nombres son de la capsula: una de taller llama `en_taller`
 * a lo que aca es `in_progress`, asi que un diccionario en el frontend le quitaria el nombre a
 * la capsula. El endpoint devuelve `estados` justamente para esto.
 *
 * Sin catalogo devuelve el id: feo, pero legible. Un hueco donde iba el estado es peor.
 */
function nombreDeEstado(estados, id) {
  return (estados || []).find((e) => e.id === id)?.name || id || '';
}

function DetalleDelCaso({ tenantId, ticketId, estados }) {
  const [caso, setCaso] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    setCaso(null); setError('');
    misSolicitudesAPI.get(tenantId, ticketId)
      .then(({ data }) => { if (vivo) setCaso(data); })
      .catch(() => { if (vivo) setError('No se pudo abrir este pedido.'); });
    return () => { vivo = false; };
  }, [tenantId, ticketId]);

  if (error) return <p className="text-[11px] text-amber-700 py-2">{error}</p>;
  if (!caso) {
    return (
      <p className="text-[11px] text-zinc-400 py-2 flex items-center gap-1.5">
        <Loader2 className="w-3 h-3 animate-spin" /> Abriendo…
      </p>
    );
  }

  const filas = [
    ['Pedido', caso.title || caso.ticket_id],
    ['Número', caso.ticket_id],
    ['Estado', nombreDeEstado(estados, caso.status)],
    ['Responsable', caso.propietario?.name || caso.propietario?.id
      || (caso.rol_propietario ? `en la cola de ${caso.rol_propietario}` : '—')],
    ['Lo pediste', formatoFecha(caso.primer_contacto_at || caso.created_at)],
    [ROTULO_FECHA_COMPROMETIDA, <FechaComprometidaCorta caso={caso} />],
    ['Última novedad', formatoFecha(caso.updated_at)],
  ];

  return (
    <div className="bg-zinc-50/70 border-l-2 border-brand-primary/40 px-3 py-2.5 my-1">
      <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1 text-[11px]">
        {filas.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-zinc-500">{k}</dt>
            <dd className="text-zinc-800">{v || '—'}</dd>
          </div>
        ))}
      </dl>
      {caso.compromiso?.frase && (
        <p className="text-[10px] text-zinc-500 mt-1">{caso.compromiso.frase}</p>
      )}
      <p className="text-[10px] text-zinc-400 mt-2">
        Si necesitás agregar algo, respondé por el mismo canal por el que lo pediste: queda en el
        mismo pedido.
      </p>
    </div>
  );
}

function TablaDeCasos({ casos, unidadPlural, priorizable = false, mostrarSolicitante = false, onReordenar, tenantId, estados }) {
  const [orden, setOrden] = useState('created_at');
  const [abierto, setAbierto] = useState(null);
  const filas = useMemo(() => {
    // Cuando el jefe priorizó, ese orden manda: ordenar por fecha encima lo haría inútil.
    if (priorizable) return casos;
    const copia = [...casos];
    copia.sort((a, b) => String(b[orden] || '').localeCompare(String(a[orden] || '')));
    return copia;
  }, [casos, orden, priorizable]);

  const mover = (desde, hasta) => {
    if (hasta < 0 || hasta >= filas.length) return;
    const copia = [...filas];
    const [fila] = copia.splice(desde, 1);
    copia.splice(hasta, 0, fila);
    onReordenar(copia.map((t) => t.ticket_id));
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-white">
          <tr className="border-b border-brand-border text-left text-[10px] uppercase tracking-wider text-zinc-500">
            {priorizable && <th className="py-2 pr-1 w-16">Orden</th>}
            <th className="py-2 pr-3">Caso</th>
            {mostrarSolicitante && <th className="py-2 pr-3">Pidió</th>}
            <th className="py-2 pr-3">Estado</th>
            <th className="py-2 pr-3">Responsable</th>
            {/* **Un clic muerto es peor que ninguno.** En modo jefe el orden lo manda la lista
                priorizada, asi que `filas` ignora `orden`: estos dos encabezados seguian con
                `cursor-pointer` y seguian llamando `setOrden`, o sea dos columnas que invitaban a
                clickear y no hacian nada. */}
            <th className={`py-2 pr-3 ${priorizable ? '' : 'cursor-pointer'}`}
                onClick={priorizable ? undefined : () => setOrden('fecha_desde')}>Desde</th>
            <th className={`py-2 pr-3 ${priorizable ? '' : 'cursor-pointer'}`}
                onClick={priorizable ? undefined : () => setOrden('fecha_hasta')}>{ROTULO_FECHA_COMPROMETIDA}</th>
            <th className="py-2 pr-3">Avance</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((t, i) => (
            <Fragment key={t.ticket_id}>
            <tr className="border-b border-zinc-50 last:border-0 align-top">
              {priorizable && (
                <td className="py-2 pr-1">
                  {/* Flechas y no drag & drop: el proyecto no tiene ninguna librería de DnD, y
                      esto es accesible por teclado, testeable, cero bundle y funciona en un
                      teléfono. Si algún día hace falta arrastrar, es una decisión propia. */}
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button" title="Subir" disabled={i === 0}
                      data-testid={`subir-${t.ticket_id}`}
                      onClick={() => mover(i, i - 1)}
                      className="p-0.5 text-zinc-400 hover:text-brand-primary disabled:opacity-25"
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button" title="Bajar" disabled={i === filas.length - 1}
                      data-testid={`bajar-${t.ticket_id}`}
                      onClick={() => mover(i, i + 1)}
                      className="p-0.5 text-zinc-400 hover:text-brand-primary disabled:opacity-25"
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                    <button
                      type="button" title="Mover al tope" disabled={i === 0}
                      data-testid={`al-tope-${t.ticket_id}`}
                      onClick={() => mover(i, 0)}
                      className="p-0.5 text-zinc-400 hover:text-brand-primary disabled:opacity-25"
                    >
                      <ChevronsUp className="w-3 h-3" />
                    </button>
                  </div>
                </td>
              )}
              <td className="py-2 pr-3">
                <button
                  type="button"
                  data-testid={`abrir-${t.ticket_id}`}
                  onClick={() => setAbierto(abierto === t.ticket_id ? null : t.ticket_id)}
                  className="text-left hover:text-brand-primary"
                >
                  <span className="font-medium text-zinc-800">{t.title || t.ticket_id}</span>
                  <span className="block text-[10px] text-zinc-400">{t.ticket_id}</span>
                </button>
              </td>
              {mostrarSolicitante && (
                <td className="py-2 pr-3 text-zinc-600" data-testid={`pidio-${t.ticket_id}`}>
                  {t.solicitante?.name || t.solicitante?.nombre || t.solicitante?.email || '—'}
                </td>
              )}
              <td className="py-2 pr-3">
                <Badge variant="outline" className="text-[10px] rounded-sm">{nombreDeEstado(estados, t.status)}</Badge>
              </td>
              <td className="py-2 pr-3"><Responsable caso={t} /></td>
              <td className="py-2 pr-3 text-zinc-600 tabular-nums">
                {formatoFecha(t.fecha_desde || t.primer_contacto_at || t.created_at)}
              </td>
              <td className="py-2 pr-3 tabular-nums">
                <FechaComprometidaCorta caso={t} />
              </td>
              <td className="py-2 pr-3"><Avance avance={t.avance} /></td>
            </tr>
            {abierto === t.ticket_id && (
              <tr>
                <td colSpan={6 + (priorizable ? 1 : 0) + (mostrarSolicitante ? 1 : 0)} className="p-0">
                  <DetalleDelCaso tenantId={tenantId} ticketId={t.ticket_id} estados={estados} />
                </td>
              </tr>
            )}
            </Fragment>
          ))}

        </tbody>
      </table>
    </div>
  );
}

function GrupoPorCapsula({ casos, porCapsula, titulo, nota, priorizable = false, mostrarSolicitante = false, onReordenar, tenantId, estados }) {
  const grupos = useMemo(() => {
    const out = {};
    casos.forEach((t) => {
      const cid = t.capsule_id || '—';
      (out[cid] = out[cid] || []).push(t);
    });
    return out;
  }, [casos]);

  if (!casos.length) return null;

  return (
    <div className="space-y-3">
      {titulo && <p className="text-[10px] uppercase tracking-wider text-zinc-500">{titulo}</p>}
      {nota && <p className="text-[10px] text-zinc-500 leading-snug -mt-1">{nota}</p>}
      {Object.entries(grupos).map(([cid, filas]) => {
        const unidad = porCapsula?.[cid]?.unidad_de_trabajo || {};
        return (
          <Card key={cid} className="rounded-sm border-brand-border">
            <CardHeader className="pb-2 border-b border-brand-border py-2 flex flex-row items-center justify-between">
              <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">
                {unidad.plural || 'Casos'} · {cid}
              </CardTitle>
              <span className="text-[10px] text-zinc-400">{filas.length}</span>
            </CardHeader>
            <CardContent className="pt-2">
              <TablaDeCasos
                casos={filas}
                unidadPlural={unidad.plural}
                priorizable={priorizable}
                mostrarSolicitante={mostrarSolicitante}
                onReordenar={(ids) => onReordenar?.(cid, ids)}
                tenantId={tenantId}
                estados={estados}
              />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

/*
 * **No hay una tabla por persona, y es una corrección del 2026-10-09.** Hasta entonces el jefe
 * veía una tabla priorizable por cada persona a cargo, y cada una mandaba sólo los ids de esa
 * persona — pero el servidor guarda **la lista entera** del jefe en la cápsula. Ordenar los casos
 * de Lucas borraba el orden de los de Pablo: las flechas parecían no hacer nada. La tabla por
 * persona había nacido en agosto para arreglar el síntoma contrario (una tabla mezclada que el
 * backend ordenaba por persona) y adaptó la pantalla a un backend que era el que estaba mal.
 *
 * Ahora es **una lista por cápsula, con lo del jefe y lo de toda su gente**, que arma el servidor
 * (`para_priorizar`) en el mismo orden que guarda.
 */

//: Cuantos casos por pagina. Alto a proposito: la mayoria de la gente tiene menos que esto y no ve
//: el control nunca; quien tiene mas, lo ve y sabe que hay mas.
const POR_PAGINA = 100;

export default function MisSolicitudesPage() {
  const { user } = useAuth();
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [incluirCerradas, setIncluirCerradas] = useState(false);
  // **Lo tipeado y lo buscado son dos cosas.** Se consulta al enviar y no en cada tecla: un
  // `regex` sobre los casos del tenant por cada letra es una consulta que nadie pidio.
  const [busqueda, setBusqueda] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  // **La paginacion existia del lado del servidor y la pantalla no la leia.** El backend devuelve
  // `total`, `offset` y `hay_mas` desde que se armo el endpoint, y el aviso de desborde que decia
  // "hay mas de lo que se muestra" se **elimino** con el comentario de que *"ahora hay paginacion
  // de verdad"* — que nunca se construyo de este lado. Resultado medido: un jefe con mas de 200
  // casos de su gente veia los primeros 200 **y nada se lo decia**.
  const [pagina, setPagina] = useState(0);


  /**
   * El orden es **una opinión con autor y es visible**: si el jefe ordena su lista y el equipo
   * no la ve, el trabajo sale en otro orden y el jefe se entera tarde.
   *
   * Se manda la lista entera y no un delta: es idempotente, y una actualización perdida
   * sobreescribe en vez de corromper.
   */
  const reordenar = async (capsuleId, ticketIds) => {
    // Optimista: la flecha tiene que responder en el momento. Si falla, se recarga y se avisa.
    setDatos((d) => {
      if (!d) return d;
      const pos = Object.fromEntries(ticketIds.map((id, i) => [id, i]));
      const lista = d.para_priorizar || [];
      const deEsta = lista
        .filter((t) => t.capsule_id === capsuleId)
        .sort((a, b) => (pos[a.ticket_id] ?? 1e9) - (pos[b.ticket_id] ?? 1e9));
      let k = 0;
      const nueva = lista.map((t) => (t.capsule_id === capsuleId ? deEsta[k++] : t));
      return { ...d, para_priorizar: nueva };
    });
    try {
      await misSolicitudesAPI.guardarOrden(user.tenant_id, capsuleId, ticketIds);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar el orden');
      cargar();
    }
  };

  const cargar = async () => {
    if (!user?.tenant_id) return;
    setLoading(true);
    try {
      // **La busqueda va al backend, no se filtra lo traido.** Filtrar en el cliente buscaria
      // solo adentro de la primera pagina: el caso de hace dos años no aparece nunca y nadie
      // entiende por que.
      const res = await misSolicitudesAPI.list(user.tenant_id, {
        incluir_cerradas: incluirCerradas,
        limit: POR_PAGINA,
        offset: pagina * POR_PAGINA,
        ...(busquedaAplicada ? { q: busquedaAplicada } : {}),
        ...(estadoFiltro ? { estado: estadoFiltro } : {}),
      });
      setDatos(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar tus solicitudes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tenant_id, incluirCerradas, busquedaAplicada, estadoFiltro, pagina]);

  // Cambiar un filtro vuelve a la primera pagina: quedarse en la cuarta con otro filtro muestra
  // una pantalla vacia que se lee como "no hay nada".
  useEffect(() => { setPagina(0); }, [incluirCerradas, busquedaAplicada, estadoFiltro]);

  const mias = datos?.mias || [];
  const deMiEquipo = datos?.de_mi_equipo || {};
  const casosDelEquipo = useMemo(
    () => Object.values(deMiEquipo).flat(),
    [deMiEquipo],
  );
  // **Ordena quien tiene gente a cargo o es responsable de una gerencia** (2026-10-09): un gerente
  // con jefes de sector debajo ordena toda su gerencia aunque no le reporten directo.
  const misGerencias = datos?.mis_gerencias || [];
  const soyJefe = (datos?.soy_jefe_de || []).length > 0 || misGerencias.length > 0;
  const ordenadosPorOtro = datos?.ordenados_por_otro || [];
  const paraPriorizar = datos?.para_priorizar || [];
  // Lo que no entra en la lista para priorizar: los terminados, cuando se pide verlos.
  // Lo que no está en ninguna de las dos listas de arriba: lo terminado, y algún pedido propio que
  // no se ordena desde acá. Sin restar lo que ordena otro, aparecería dos veces.
  const terminadosDelJefe = useMemo(() => {
    const enLaLista = new Set([
      ...paraPriorizar.map((t) => t.ticket_id),
      ...ordenadosPorOtro.flatMap((g) => (g.casos || []).map((t) => t.ticket_id)),
    ]);
    return [...mias, ...casosDelEquipo].filter((t) => !enLaLista.has(t.ticket_id));
  }, [paraPriorizar, ordenadosPorOtro, mias, casosDelEquipo]);
  const autorizaciones = datos?.autorizaciones_para_mi || [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#09090B]">Mis solicitudes</h1>
          {/* El par "Mi trabajo" / "Mis solicitudes" se confunde: el subtitulo los separa.
              Y para quien tiene gente a cargo dice **las dos cosas**: el nombre de la pantalla
              anuncia una sola, y el modo jefe —la tabla priorizable de abajo— vivio meses sin
              que nadie supiera que existia. Ni Diego, que lo habia pedido, lo encontraba: lo
              busco por el menu el 2026-08-31 y concluyo que no se habia construido. */}
          <p className="text-xs text-zinc-500 mt-0.5">
            {soyJefe
              ? 'Lo que pediste, y lo que pidió tu gente — que podés ordenar por prioridad. (Lo que trabajás está en Mi trabajo.)'
              : 'Lo que pediste. (Lo que trabajás está en Mi trabajo.)'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <form
            onSubmit={(e) => { e.preventDefault(); setBusquedaAplicada(busqueda.trim()); }}
            className="flex items-center gap-1"
          >
            <input
              type="search"
              data-testid="buscar-solicitud"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por titulo o numero"
              className="h-8 w-52 text-xs border border-brand-border rounded-sm px-2"
            />
            {busquedaAplicada && (
              <button
                type="button"
                onClick={() => { setBusqueda(''); setBusquedaAplicada(''); }}
                className="text-[11px] text-zinc-500 hover:text-brand-primary"
              >
                limpiar
              </button>
            )}
          </form>
          <select
            data-testid="filtro-estado"
            value={estadoFiltro}
            onChange={(e) => setEstadoFiltro(e.target.value)}
            className="h-8 text-xs border border-brand-border rounded-sm px-2"
          >
            <option value="">Todos los estados</option>
            {(datos?.estados || []).map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-600">
            <input
              type="checkbox"
              checked={incluirCerradas}
              onChange={(e) => setIncluirCerradas(e.target.checked)}
              disabled={!!estadoFiltro}
              title={estadoFiltro ? 'Al filtrar por un estado concreto ya se muestran los cerrados' : ''}
            />
            Ver tambien lo cerrado
          </label>
          <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={cargar}>
            <RefreshCw className="w-3 h-3 mr-1" /> Actualizar
          </Button>
        </div>
      </div>

      {(datos?.avisos || []).map((aviso) => (
        <div key={aviso} className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{aviso}</span>
        </div>
      ))}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <KpiCard label="Pedidos míos" value={mias.length} icon={Inbox} loading={loading} />
        {soyJefe && (
          <>
            <KpiCard label="De mi gente" value={casosDelEquipo.length} icon={Users} loading={loading} />
            <KpiCard label="Personas a cargo" value={(datos?.soy_jefe_de || []).length} loading={loading} />
          </>
        )}
      </div>

      {/* **Arriba de todo, y antes de los casos propios.** Es lo único de esta pantalla que
          espera una acción de esta persona y que le bloquea el día a otra: los casos propios se
          miran, una autorización se responde. Y para el autorizante de otra gerencia —que no
          tiene cápsula y no ve «Mi trabajo»— ésta es la única superficie que existe. */}
      {!loading && !!autorizaciones.length && (
        <div className="border border-amber-200 bg-amber-50/50 rounded-sm p-3" data-testid="mis-autorizaciones">
          <p className="text-sm font-medium text-amber-900 mb-0.5">
            Esperan tu autorización ({autorizaciones.length})
          </p>
          <p className="text-[11px] text-amber-800 mb-2">
            Tu respuesta se registra y se avisa. <b>No frena el caso</b>: qué hacer con ella lo
            decide quien lo tiene a cargo.
          </p>
          {/* Ayuda de operación, no de configuración: acá nadie parametriza nada, pero la
              decisión tiene consecuencias sobre el trabajo de otros y conviene saberlas antes
              de apretar. */}
          <QueSeConfiguraAca
            testId="ayuda-mis-autorizaciones"
            queEs="Los casos de otra gente que esperan que vos des el visto bueno."
            paraQue="Hasta ahora esto pasaba por mail: alguien contestaba «se autoriza» en un hilo y esa decisión no quedaba en ningún lado. Acá queda registrada, con quién, cuándo y por qué."
            aQueAfecta={<>A <b>nada automáticamente</b>. Se registra tu decisión y se le avisa a quien corresponda. El caso <b>no se frena ni cambia de estado</b>: qué hacer con un rechazo lo decide quien lo tiene a cargo.</>}
            ejemplo={<>Alguien de Compras pide algo, el área que lo atiende pide tu autorización como gerente de su área, y te aparece acá. Aprobás o rechazás, con un comentario si querés, y sigue su curso.</>}
            ojo={<>Si <b>rechazás</b> y el caso tenía otras autorizaciones esperando, esas se <b>cancelan</b>: nadie firma algo que ya no va a pasar. El caso sigue abierto igual, así que pueden volver a pedírtelo si lo replantean. El comentario es opcional, pero sin motivo el que recibe el rechazo no sabe si insistir, corregir algo o dejarlo.</>}
          />
          <div className="space-y-2">
            {autorizaciones.map((a) => (
              <div key={a.task_id} className="bg-white border border-brand-border rounded-sm p-2">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-medium">{a.caso?.title || a.title}</span>
                  <span className="text-[10px] text-zinc-500 font-mono">{a.ticket_id}</span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Lo pidió {a.caso?.solicitante?.nombre || a.caso?.solicitante?.email || 'alguien'}
                  {a.caso?.fecha_hasta ? ` · compromiso ${String(a.caso.fecha_hasta).slice(0, 10)}` : ''}
                </p>
                {a.caso?.description && (
                  <p className="text-[11px] text-zinc-600 mt-1 line-clamp-3 whitespace-pre-wrap">
                    {a.caso.description}
                  </p>
                )}
                <div className="mt-2">
                  <Autorizacion
                    tarea={a}
                    tenantId={user?.tenant_id}
                    pendientesEnElCaso={
                      autorizaciones.filter((o) => o.ticket_id === a.ticket_id).length
                    }
                    onCambio={cargar}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
      ) : (
        <>
          {soyJefe ? (
            <>
              {misGerencias.length > 0 && (
                <p className="text-[11px] text-zinc-600" data-testid="mis-gerencias">
                  Sos responsable de <b>{misGerencias.map((g) => g.gerencia).join(', ')}</b>: el orden
                  de adentro lo definís vos, y nadie por encima lo puede invertir.
                </p>
              )}
              <GrupoPorCapsula
                casos={paraPriorizar}
                porCapsula={datos?.por_capsula}
                titulo="Para priorizar — lo que pediste vos y lo que pidió tu gente"
                nota="Ordená todo lo abierto de tu área, lo tuyo incluido, en el orden en que querés que
                      se atienda. Lo ve quien trabaja los casos en la bandeja de Casos, cuando elige
                      «Por prioridad». Por omisión esa bandeja ordena por fecha
                      comprometida, igual que Ejecutivo y el Gantt."
                priorizable
                mostrarSolicitante
                onReordenar={reordenar}
                tenantId={user?.tenant_id}
                estados={datos?.estados}
              />
              {/* **Lo que ordena otro responsable, sin flechas y con el aviso** (2026-10-09). Se
                  muestra porque es trabajo de tu gente; no se ofrece moverlo porque el servidor lo
                  rechazaría — el orden dentro de una gerencia es de su responsable. */}
              {ordenadosPorOtro.map((grupo) => (
                <div key={`${grupo.capsule_id}-${grupo.gerencia_id}`} className="space-y-1" data-testid={`ordenado-por-otro-${grupo.gerencia_id}`}>
                  <p className="text-[11px] text-zinc-500">{grupo.aviso}</p>
                  <GrupoPorCapsula
                    casos={grupo.casos}
                    porCapsula={datos?.por_capsula}
                    titulo={`${grupo.gerencia} — lo ordena ${grupo.responsable?.nombre || 'su responsable'}`}
                    mostrarSolicitante
                    tenantId={user?.tenant_id}
                    estados={datos?.estados}
                  />
                </div>
              ))}
              <GrupoPorCapsula
                casos={terminadosDelJefe}
                porCapsula={datos?.por_capsula}
                titulo="Terminados y otros pedidos"
                mostrarSolicitante
                tenantId={user?.tenant_id}
                estados={datos?.estados}
              />
            </>
          ) : (
            <GrupoPorCapsula casos={mias} porCapsula={datos?.por_capsula} titulo="Lo que pedí" tenantId={user?.tenant_id} estados={datos?.estados} />
          )}
          {!mias.length && !casosDelEquipo.length && (
            <p className="text-center py-12 text-xs text-zinc-400">
              {pagina > 0
                ? 'Esta página quedó vacía. Volvé a la anterior.'
                : 'No hay solicitudes tuyas todavía.'}
            </p>
          )}

          {/* **Decir cuántos hay, no sólo mostrar algunos.** El numero total es la mitad que
              importa: sin el, "200 casos" y "200 de 840" se leen igual. */}
          {(datos?.total || 0) > POR_PAGINA && (
            <div className="flex items-center justify-between gap-3 pt-1" data-testid="paginacion">
              <p className="text-[11px] text-zinc-500">
                {`Mostrando ${(datos.offset || 0) + 1}–${(datos.offset || 0) + mias.length + casosDelEquipo.length} de ${datos.total}`}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 text-[11px] rounded-sm"
                  disabled={pagina === 0 || loading}
                  onClick={() => setPagina((p) => Math.max(0, p - 1))}
                  data-testid="pagina-anterior">
                  Anterior
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-[11px] rounded-sm"
                  disabled={!datos?.hay_mas || loading}
                  onClick={() => setPagina((p) => p + 1)}
                  data-testid="pagina-siguiente">
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
